import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

// Continuous "read the series" navigation: an accordion list of every article in the
// category (jump or skip to any), plus a prev/next bar so visitors can move article-to-article.
export default function SeriesNav({ base, tagline, siblings, currentSlug, prev, next }) {
  const currentIndex = siblings.findIndex((s) => s.slug === currentSlug);

  return (
    <section className="bg-white border-t border-slate-100 py-14">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="overline">Continue the Series · {tagline}</span>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mt-2 mb-6">
          Read the full series, or jump to what matters
        </h2>

        <Accordion type="single" collapsible defaultValue={currentSlug} className="border border-slate-200 rounded-2xl overflow-hidden" data-testid="series-accordion">
          {siblings.map((s, i) => {
            const isCurrent = s.slug === currentSlug;
            const done = i < currentIndex;
            return (
              <AccordionItem key={s.slug} value={s.slug} className="border-b border-slate-100 last:border-b-0 px-5" data-testid={`series-item-${s.slug}`}>
                <AccordionTrigger className="hover:no-underline">
                  <span className="flex items-center gap-3">
                    <span className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0 ${isCurrent ? "bg-brand text-white" : done ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                      {done ? <Check size={13} /> : i + 1}
                    </span>
                    <span className="flex flex-col text-left">
                      <span className={`text-sm font-semibold ${isCurrent ? "text-brand" : "text-slate-800"}`}>{s.navLabel}</span>
                      {isCurrent && <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">You are here</span>}
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pl-10">
                  <p className="text-slate-500 text-sm leading-relaxed mb-3">{s.lede}</p>
                  {isCurrent ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400">Currently reading</span>
                  ) : (
                    <Link to={`${base}/${s.slug}`} data-testid={`series-read-${s.slug}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:gap-2.5 transition-all">
                      Read {s.navLabel} <ArrowRight size={14} />
                    </Link>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          {prev ? (
            <Link to={`${base}/${prev.slug}`} data-testid="series-prev" className="flex-1 group border border-slate-200 rounded-xl p-4 hover:border-brand transition-colors">
              <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400"><ArrowLeft size={13} /> Previous</span>
              <span className="block text-sm font-semibold text-slate-800 mt-1 group-hover:text-brand transition-colors">{prev.navLabel}</span>
            </Link>
          ) : <div className="flex-1 hidden sm:block" />}
          {next ? (
            <Link to={`${base}/${next.slug}`} data-testid="series-next" className="flex-1 group bg-[#0B203E] rounded-xl p-4 hover:bg-brand transition-colors text-right">
              <span className="flex items-center justify-end gap-1.5 text-[11px] font-bold uppercase tracking-wide text-white/50">Next Article <ArrowRight size={13} /></span>
              <span className="block text-sm font-semibold text-white mt-1">{next.navLabel}</span>
            </Link>
          ) : <div className="flex-1 hidden sm:block" />}
        </div>
      </div>
    </section>
  );
}
